/**
 * octopus.js — uploading queued match records to the Octopus server on the Pi.
 *
 * The durable queue already exists: `queue.js` stores every record at capture
 * (`Scout.js` calls `enqueueRecord` straight after validation), gives it an `id` - the
 * match's own, from `lib/submissionId.js`, which its saved copy and QR carry too - and
 * stamps `_queued_at`. This module maps those onto Octopus's envelope and drains them.
 *
 * It never removes anything from that queue. Removal is only a deliberate delete in
 * `Relay.js` (× or Clear Queue - its uploads keep records too), and a record dropped
 * here would vanish from the USB path. Instead this
 * keeps its own ledger of ids the Pi has accepted, mirroring the `_submissions` table
 * the server keeps for the same reason: a record can safely go both ways.
 */

import { getQueue, getTimekeeperQueue } from "./queue";
import { SUBMISSION_ID_KEY, EDITED_AT_KEY } from "./submissionId";

const SERVER_KEY = "octopus_server";

/**
 * What goes to the Pi, by base_name. Each has its own queue and its own ledgers of ids
 * the Pi accepted or refused, per event. The scouting keys are unchanged from before
 * timekeeper existed, so ledgers already on a laptop keep working.
 *
 * "timekeeper" is stored by the Pi in <event>_timekeeper.db, beside the main database.
 */
const STREAMS = {
    scouting: {
        queue: getQueue,
        accepted: (eventPath) => `octopus_accepted_${eventPath || "GLOBAL"}`,
        rejected: (eventPath) => `octopus_rejected_${eventPath || "GLOBAL"}`,
    },
    timekeeper: {
        queue: getTimekeeperQueue,
        accepted: (eventPath) => `octopus_accepted_timekeeper_${eventPath || "GLOBAL"}`,
        rejected: (eventPath) => `octopus_rejected_timekeeper_${eventPath || "GLOBAL"}`,
    },
};

export const BASE_NAMES = Object.keys(STREAMS);

function stream(baseName) {
    const found = STREAMS[baseName];
    if (!found) throw new Error(`unknown Octopus stream: ${baseName}`);
    return found;
}

/** `server.app.MAX_BATCH`. A larger batch is a 413, not a silent truncation. */
export const MAX_BATCH = 50;

/**
 * Queue fields that belong to the envelope or to this app, not to the scout's record.
 * These must be lifted out of `data`: `_queued_at` is a legal SQL identifier, so the
 * server would accept them as scouting columns and bake them into the table shape.
 * `submission_id` is already the record's `id` once queued; it is listed in case a
 * record ever arrives with it still in place, which would split the event's table.
 */
const ENVELOPE_FIELDS = ["id", "_queued_at", "_gamemode", "_edited_at", SUBMISSION_ID_KEY, EDITED_AT_KEY];

/**
 * The id the Pi stores this version of a record under: the match's own id, plus its edit
 * time once it has been edited. So an edit reaches the Pi as a new version - the Pi keeps
 * every version, and its sync treats the newest capture as the current one - instead of
 * being dropped as a duplicate of the original. Unedited records keep the plain id, so
 * ledgers already on a laptop are unchanged.
 */
export function piKey(record) {
    return record._edited_at ? `${record.id}@${record._edited_at}` : record.id;
}

// --- configuration ----------------------------------------------------------
//
// Only the server address is configured, and blank means "find the Pi". The Pi needs
// no token: it accepts every event registered and open on it. Osprey still sends its
// event code as one (see eventToken), which the Pi ignores unless it was started with
// --token <event code>.

/**
 * The Pi's two addresses, tried together. The name needs the laptop's DNS to answer
 * 10.42.0.1, and Tailscale MagicDNS, a school's DNS filter, a router's rebinding
 * protection or an empty answer cached from before the cable went in all stop that -
 * each one looks exactly like "Pi: offline". The IP address needs no DNS: the Pi serves
 * plain HTTP on 8080 beside HTTPS (apps/octopus/server/__main__.py), and Chrome lets an
 * HTTPS page reach a private IP over HTTP once the site is allowed local network access.
 */
export const PI_ADDRESSES = ["https://pi.db-hub.me:8000", "http://10.42.0.1:8080"];
const PI_HOSTS = new Set(PI_ADDRESSES.map((address) => new URL(address).hostname));
const OFF = "off";

function isPiAddress(server) {
    try {
        return PI_HOSTS.has(new URL(server).hostname);
    } catch {
        return false;
    }
}

/**
 * Where to look for the Pi, in order. Blank: both Pi addresses. A Pi address: that one,
 * then the other as a backup. Anything else (a dev server) alone, so records meant for it
 * never go to the real Pi. "off": nowhere - Firebase only.
 */
export function octopusServers() {
    const { server } = getOctopusConfig();
    if (server.toLowerCase() === OFF) return [];
    if (!server) return PI_ADDRESSES;
    if (!isPiAddress(server)) return [server];
    return [server, ...PI_ADDRESSES.filter((address) => new URL(address).hostname !== new URL(server).hostname)];
}

/** The address that answered the last health check; records go there. */
let activeServer = null;

function serverUrl() {
    const servers = octopusServers();
    return servers.includes(activeServer) ? activeServer : servers[0];
}

export function getOctopusConfig() {
    try {
        return { server: (localStorage.getItem(SERVER_KEY) || "").trim().replace(/\/+$/, "") };
    } catch {
        return { server: "" };
    }
}

export function setOctopusConfig({ server }) {
    try {
        if (server !== undefined) localStorage.setItem(SERVER_KEY, String(server).trim());
    } catch {}
}

/** What Osprey sends as a bearer token: the event code. Ignored by a Pi with no token. */
export function eventToken(eventPath) {
    return String(eventPath || "").trim();
}

export function isOctopusConfigured() {
    return octopusServers().length > 0;
}

// --- the accepted / rejected ledgers ----------------------------------------

function readIdSet(key) {
    try {
        const raw = JSON.parse(localStorage.getItem(key) || "[]");
        return new Set(Array.isArray(raw) ? raw : []);
    } catch {
        return new Set();
    }
}

function writeIdSet(key, set) {
    try {
        localStorage.setItem(key, JSON.stringify([...set]));
    } catch {}
}

export function getAcceptedIds(eventPath, baseName = "scouting") {
    return readIdSet(stream(baseName).accepted(eventPath));
}

export function getRejectedIds(eventPath, baseName = "scouting") {
    return readIdSet(stream(baseName).rejected(eventPath));
}

function markIds(key, ids) {
    if (!ids.length) return;
    const set = readIdSet(key);
    ids.forEach((id) => set.add(id));
    writeIdSet(key, set);
}

/** Records still owed to the Pi: queued, not accepted, and not already refused. */
export function pendingForOctopus(eventPath, baseName = "scouting") {
    const accepted = getAcceptedIds(eventPath, baseName);
    const rejected = getRejectedIds(eventPath, baseName);
    return stream(baseName).queue(eventPath).filter((r) => r.id && !accepted.has(piKey(r)) && !rejected.has(piKey(r)));
}

/** Everything still owed to the Pi, every stream together - what the status chip shows. */
export function pendingCount(eventPath) {
    return BASE_NAMES.reduce((sum, base) => sum + pendingForOctopus(eventPath, base).length, 0);
}

// --- the envelope -----------------------------------------------------------

/**
 * Split one queued record into Octopus's envelope and the scout's own columns.
 *
 * `gamemode` moves *into* `data` deliberately: the envelope has no slot for it, and
 * without it a quals match 17 and an elims match 17 are the same record. `version`
 * is left in place — the Firebase path strips it, this one must not, since it is the
 * only pointer from a stored row back to the config that produced it.
 */
export function toSubmission(record) {
    const data = { ...record };
    ENVELOPE_FIELDS.forEach((field) => delete data[field]);
    if (record._gamemode !== undefined && record._gamemode !== null) {
        data.gamemode = record._gamemode;
    }

    const submission = { submission_id: piKey(record), data };
    const captured = record._edited_at || record._queued_at;
    if (captured) {
        // Fixed at capture (or at the edit) by the queue, not here. Re-minting at send time
        // would produce a fresh id on every retry and defeat the server's ledger entirely.
        // An edit is captured when it was made, so the Pi's sync sees it as the newer one.
        submission.captured_at = new Date(captured).toISOString();
    }
    return submission;
}

/** `2026cur` -> `{ year: 2026, competition: "2026cur" }`. The slug is the whole code. */
export function toCompetition(eventPath) {
    const path = String(eventPath || "").trim();
    const year = parseInt(path.substring(0, 4), 10);
    if (!path || Number.isNaN(year)) return null;
    return { year, competition: path };
}

// --- talking to the Pi ------------------------------------------------------

/**
 * How long a request to the Pi may take before it is abandoned, in ms.
 *
 * Unplugging the cable can leave a request waiting on a dead connection for minutes -
 * the laptop keeps retrying it, further apart each time. Without a limit, checks piled
 * up every 10 seconds until all six of the browser's connections to the Pi were stuck,
 * and after the cable went back in nothing could get out: the status stayed offline
 * until the server was restarted and closed them. Aborting frees the connection.
 * Exported so tests can shorten it.
 */
export const OCTOPUS_TIMEOUTS = { health: 3000, submit: 15000 };

/**
 * fetch, abandoned after `ms`. Aborts the request so the browser drops the connection,
 * and also races a timer, so even a fetch that ignores the abort cannot hang the caller.
 */
async function fetchWithTimeout(fetchImpl, url, options, ms) {
    const controller = typeof AbortController === "function" ? new AbortController() : null;
    let timer;
    const timedOut = new Promise((_, reject) => {
        timer = setTimeout(() => {
            // Reject first, so the race settles on "timed out" rather than on the
            // browser's generic "aborted" - the reason the status chip shows.
            reject(new Error(`timed out after ${Math.round(ms / 1000)}s`));
            if (controller) controller.abort();
        }, ms);
    });
    try {
        return await Promise.race([
            fetchImpl(url, controller ? { ...options, signal: controller.signal } : options),
            timedOut,
        ]);
    } finally {
        clearTimeout(timer);
    }
}

/** One address's /health. Never rejects. */
async function probeHealth(fetchImpl, server, ms) {
    try {
        const res = await fetchWithTimeout(fetchImpl, `${server}/api/v1/health`, {
            method: "GET",
            cache: "no-store",
        }, ms);
        if (!res.ok) return { reachable: false, reason: `HTTP ${res.status}`, status: res.status };
        const body = await res.json();
        return { reachable: true, competitions: body.competitions || [], server };
    } catch (err) {
        return { reachable: false, reason: err && err.message ? err.message : "unreachable" };
    }
}

/**
 * The first answer that reached the Pi, or null once every one has failed - or undefined
 * when `waitMs` passes with neither.
 */
function firstReachable(answers, waitMs = Infinity) {
    return new Promise((resolve) => {
        let left = answers.length;
        const timer = Number.isFinite(waitMs) ? setTimeout(() => resolve(undefined), waitMs) : null;
        const settle = (value) => {
            clearTimeout(timer);
            resolve(value);
        };
        answers.forEach((answer) => answer.then((result) => {
            if (result.reachable) settle(result);
            else if (--left === 0) settle(null);
        }));
    });
}

/**
 * How often a health check that has heard nothing asks again, on a fresh connection, until
 * its time limit. A request sent while the cable was out can hang on a dead route until that
 * limit, so without this, plugging in mid-check waited out the old request before asking
 * again - several seconds before the chip turned green. Exported so tests can change it.
 */
export const HEALTH_RESEND = { ms: 1000 };

/**
 * How long a check waits for the first address (the name, unless one is typed) once a later
 * one has answered. Plain HTTP skips the TLS handshake, so on the switch the IP answers in
 * ~15 ms and the name in ~250 ms: without this the IP won every race, even on a laptop whose
 * DNS works. A name that fails (no DNS) fails at once and costs nothing; one that hangs costs
 * this much, counted from the start of the check. Exported so tests can change it.
 */
export const PREFER_FIRST = { ms: 1000 };

/**
 * Has the user (or a school's policy) blocked this site from the local network? Chrome
 * then fails every request to the Pi with the same "Failed to fetch" as an unplugged
 * cable, and the fix is in the browser's site settings, not the cable.
 */
async function localNetworkBlocked() {
    try {
        if (typeof navigator === "undefined" || !navigator.permissions) return false;
        for (const name of ["local-network", "local-network-access"]) {
            try {
                if ((await navigator.permissions.query({ name })).state === "denied") return true;
            } catch {}
        }
    } catch {}
    return false;
}

/**
 * Is the Pi reachable? `/health` is unauthenticated on purpose, precisely so this
 * answers "the Pi is down" separately from "my token is wrong". Every Pi address is
 * asked at once, and records go to the one that answered until a check says otherwise:
 * the first address (the name) whenever it answers within PREFER_FIRST, else whichever did.
 */
export async function checkHealth(fetchImpl = fetch, ms = OCTOPUS_TIMEOUTS.health) {
    const servers = octopusServers();
    if (!servers.length) return { reachable: false, reason: "not configured" };
    const started = Date.now();
    const answers = servers.map((server) => probeHealth(fetchImpl, server, ms));
    let found;
    for (;;) {
        const left = ms - (Date.now() - started);
        found = await firstReachable(answers, left > HEALTH_RESEND.ms ? HEALTH_RESEND.ms : Infinity);
        if (found !== undefined) break;
        // Nothing yet: ask again on a new connection, ending by the same time limit.
        const remaining = ms - (Date.now() - started);
        servers.forEach((server) => answers.push(probeHealth(fetchImpl, server, remaining)));
    }
    if (found && found.server !== servers[0]) {
        // `answers` holds one probe per server per round, in `servers` order.
        const first = answers.filter((_, i) => i % servers.length === 0);
        const better = await firstReachable(first, Math.max(0, PREFER_FIRST.ms - (Date.now() - started)));
        if (better) found = better;
    }
    if (found) {
        activeServer = found.server;
        return found;
    }
    if (await localNetworkBlocked()) {
        return { reachable: false, blocked: true, reason: "this browser is blocking local network access for this site - allow it in the site settings" };
    }
    // A refusal (403: reached the Pi, but not over the switch) says more than "Failed to fetch".
    const all = await Promise.all(answers);
    const { status, ...failure } = all.find((answer) => answer.status) || all[0];
    return failure;
}

/**
 * Why the Pi refused a whole batch, in words a scout can act on. Each holds the queue:
 * nothing is lost, and the records go up once the cause is fixed.
 */
export const REFUSALS = {
    400: "the Pi could not read the event code",
    401: "the Pi wants a token (started with --token)",
    403: "this laptop is not on the scouting network",
    404: "this event is not registered on the Pi",
    409: "this event is closed on the Pi",
};

/**
 * Send one batch. Returns what the caller should do next, never throws.
 *
 * The status policy is the server's, restated: 400/401/403/404/409 are configuration
 * errors that retrying cannot fix, so the queue is held and the scout is told loudly
 * rather than the records being discarded — losing a day of scouting to a typo in the
 * server address would be far worse than a stalled queue. 413 splits. 503 and network
 * failures keep everything queued and back off.
 */
async function sendBatch(batch, eventPath, fetchImpl, baseName = "scouting") {
    const server = serverUrl();
    const token = eventToken(eventPath);
    const competition = toCompetition(eventPath);
    if (!competition) return { outcome: "config", error: `unusable event path: ${eventPath}` };

    let res;
    try {
        res = await fetchWithTimeout(fetchImpl, `${server}/api/v1/submit`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({
                year: competition.year,
                competition: competition.competition,
                base_name: baseName,
                records: batch.map(toSubmission),
            }),
        }, OCTOPUS_TIMEOUTS.submit);
    } catch (err) {
        return { outcome: "unreachable", error: err && err.message ? err.message : "network error" };
    }

    if (res.status === 413) return { outcome: "split" };
    if (res.status === 503) return { outcome: "busy" };
    if (REFUSALS[res.status]) {
        return { outcome: "config", error: REFUSALS[res.status], status: res.status };
    }
    if (res.status === 422) {
        // The payload itself is malformed. Retrying will never help.
        return { outcome: "rejected", ids: batch.map(piKey) };
    }
    if (!res.ok) return { outcome: "busy", error: `HTTP ${res.status}` };

    let body;
    try {
        body = await res.json();
    } catch {
        return { outcome: "busy", error: "unreadable response" };
    }

    const accepted = [];
    const rejected = [];
    (body.results || []).forEach((r) => {
        // `duplicate` is a success: the record is on the Pi, put there by an earlier
        // attempt whose response was lost. That is the whole point of the id.
        if (r.status === "stored" || r.status === "duplicate") accepted.push(r.submission_id);
        else if (r.status === "rejected") rejected.push(r.submission_id);
    });
    return { outcome: "sent", accepted, rejected };
}

/**
 * Drain everything currently owed to the Pi, in batches.
 * Returns a summary; never throws, so a caller can poll it on a timer.
 */
export async function uploadPending(eventPath, fetchImpl = fetch, baseName = "scouting") {
    if (!isOctopusConfigured()) return { ran: false, reason: "not configured" };

    const keys = stream(baseName);
    const pending = pendingForOctopus(eventPath, baseName);
    if (!pending.length) return { ran: true, accepted: 0, rejected: 0, pending: 0 };

    let accepted = 0;
    let rejected = 0;
    let size = MAX_BATCH;

    for (let i = 0; i < pending.length; ) {
        const batch = pending.slice(i, i + size);
        const result = await sendBatch(batch, eventPath, fetchImpl, baseName);

        if (result.outcome === "split") {
            if (size === 1) return { ran: true, accepted, rejected, error: "server refused a single record" };
            size = Math.max(1, Math.floor(size / 2));
            continue; // same offset, smaller batch
        }
        if (result.outcome === "config" || result.outcome === "unreachable" || result.outcome === "busy") {
            return { ran: true, accepted, rejected, halted: result.outcome, error: result.error };
        }
        if (result.outcome === "rejected") {
            markIds(keys.rejected(eventPath), result.ids);
            rejected += result.ids.length;
            i += batch.length;
            continue;
        }

        markIds(keys.accepted(eventPath), result.accepted);
        markIds(keys.rejected(eventPath), result.rejected);
        accepted += result.accepted.length;
        rejected += result.rejected.length;
        i += batch.length;
    }

    return { ran: true, accepted, rejected, pending: pendingForOctopus(eventPath, baseName).length };
}

/** True while a sync is sending, so overlapping checks never send a record twice. */
/**
 * The sync in progress, if any. A new call while one runs gets the same answer rather
 * than starting another, so checks never pile up behind a slow or dead Pi - and because
 * every request has a time limit, a stuck sync always ends.
 */
let currentSync = null;

/**
 * Check the Pi and, if it answers and anything is waiting, send it - every stream.
 *
 * This is what makes uploads automatic. Export sends at once, but a first try can fail
 * (the Pi briefly unreachable, a permission prompt still open), and before this nothing
 * retried until a scout tapped the Pi status. The status chip runs this on every health
 * check, so a waiting record goes up within one interval of the Pi being reachable.
 *
 * A held queue (unregistered or closed event, wrong network) stays pending; it is re-sent
 * each time, which costs one small request and means a fix takes effect without a tap.
 * Why it is held comes back as `refused`, so the chip can say so. Never throws.
 */
export function syncWithPi(eventPath, fetchImpl = fetch) {
    if (!currentSync) {
        currentSync = runSync(eventPath, fetchImpl).finally(() => {
            currentSync = null;
        });
    }
    return currentSync;
}

async function runSync(eventPath, fetchImpl) {
    const health = await checkHealth(fetchImpl);
    let refused = null;
    if (health.reachable && pendingCount(eventPath) > 0) {
        try {
            const results = await uploadAllPending(eventPath, fetchImpl);
            const held = Object.values(results).find((r) => r && r.halted === "config");
            if (held) refused = held.error;
        } catch {
            // uploadPending does not throw; this is only belt and braces.
        }
    }
    return {
        reachable: health.reachable,
        reason: !health.reachable ? health.reason : refused ? `connected, but ${refused}` : "connected",
        refused,
        pending: pendingCount(eventPath),
        ...(health.blocked ? { blocked: true } : {}),
        // Which address answered, so the chip can say whether the name (DNS) or the IP is in use.
        ...(health.server ? { server: health.server } : {}),
    };
}

/** Drain every stream in turn. One failing (say, a 404) does not stop the others. */
export async function uploadAllPending(eventPath, fetchImpl = fetch) {
    const results = {};
    for (const base of BASE_NAMES) {
        results[base] = await uploadPending(eventPath, fetchImpl, base);
    }
    return results;
}

/** Requeue everything previously refused, after the cause has been fixed. */
export function retryRejected(eventPath, baseName = "scouting") {
    const count = getRejectedIds(eventPath, baseName).size;
    try {
        localStorage.removeItem(stream(baseName).rejected(eventPath));
    } catch {}
    return count;
}
